import { useSyncExternalStore } from 'react';

/** The project was changed in another tab while this page shows a workspace that choice does not name (Design spec E6, amended). */
interface TabChange {
  /** What the other tab switched to, as a toast names it: `Default · Acme`. */
  target: string;
  /** Whose workspace this page still shows. */
  current: string;
  reload: () => void;
  dismiss: () => void;
}

/** The one notice both the page banner of a shared console and the app's notice say: the first line and the second. */
export function tabChangeLines(change: Pick<TabChange, 'target' | 'current'>): { title: string; body: string } {
  return { title: `You switched to ${change.target} in another tab`, body: `This page still shows ${change.current}.` };
}

let change: TabChange | null = null;
let claimed = false;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The app's notice publishes the change (null when there is none); a console banner that is on screen shows it with its own text. */
export function publishTabChange(next: TabChange | null): void {
  change = next;
  emit();
}

/** A console that draws the "Shared with you" banner takes over the tab notice, so the page has one block, not two. */
export function claimTabChange(active: boolean): () => void {
  if (!active) return () => undefined;
  claimed = true;
  emit();
  return () => {
    claimed = false;
    emit();
  };
}

/** The change to show in a banner, or null. */
export function useTabChange(): TabChange | null {
  return useSyncExternalStore(subscribe, () => change, () => null);
}

/** Whether a banner on the page has taken the change over (the app's own notice then draws nothing). */
export function useTabChangeClaimed(): boolean {
  return useSyncExternalStore(subscribe, () => claimed, () => false);
}
